import { Navigate } from 'react-router';
import { CustomerLoginInfo } from 'orderme-api-integration-client';
import { RootState } from './redux/store';
import usePrepareLink from './popups/hooks/usePrepareLink';
import { GET_ENUMS } from './popups/router';
import { useAppSelector } from './redux/hooks';
import { RouterParam } from './helpers/routerParam';
import { useRouteNavigator } from './routes/useRouteNavigator';
import { getRedirectAfterLogin, removeRedirectAfterLogin } from './helpers/localStorageService';

export type AuthRouteProps = {
  type: 'guest' | 'authenticated' | 'onlyNotAuthenticated';
  approved?: boolean;
  isLoginPage?: boolean;
  children: React.JSX.Element;
};

function AuthRoute(props: AuthRouteProps) {
  const loginInfo: CustomerLoginInfo = useAppSelector((state: RootState) => state.customerState.loginInfo);
  const isPasswordRestored: boolean = useAppSelector((state: RootState) => state.authState.isPasswordRestored);
  const routeNavigator = useRouteNavigator();

  const confirmPhoneLink = usePrepareLink({
    query: {
      [RouterParam.Popup]: GET_ENUMS.popup.confirmPhone,
    },
  });
  const confirmEmailLink = usePrepareLink({
    query: {
      [RouterParam.Popup]: GET_ENUMS.popup.confirmEmail,
    },
  });

  const { type, approved, isLoginPage } = props;
  const isAuthenticated = loginInfo?.customers?.length;

  if (isLoginPage && isAuthenticated) {
    // If user is authenticated and on login page, redirect to previous URL or home
    const redirectAfterLogin = getRedirectAfterLogin();
    if (redirectAfterLogin) {
      removeRedirectAfterLogin();
      return <Navigate to={redirectAfterLogin} replace />;
    }

    return <Navigate to={routeNavigator.getHomePath()} replace />;
  }

  if (
    (type === 'authenticated' && (!isAuthenticated || isPasswordRestored)) ||
    (type === 'onlyNotAuthenticated' && isAuthenticated)
  ) {
    return <Navigate to={routeNavigator.getHomePath()} replace />;
  }

  if (isAuthenticated && approved && (!loginInfo.emailConfirmed || !loginInfo.phoneConfirmed)) {
    const confirmLink = !loginInfo.phoneConfirmed ? confirmPhoneLink : confirmEmailLink;
    return <Navigate to={{ pathname: routeNavigator.getHomePath(), search: confirmLink.search }} replace />;
  }

  return props.children;
}

export default AuthRoute;
